import { useState } from 'react';
import 'twin.macro';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import IntervieweeCard from '@/components/IntervieweeCard';
import IntervieweeCardSkeleton from '@/components/IntervieweeCardSkeleton';
import ResetInterviewModal from '@/components/ResetInterviewModal';
import { SessionStatus } from '@/libs/graphql';
import {
  useCreateSessionViewTokenMutation,
  useResetInterviewSessionMutation,
} from '@/libs/graphql';
import { FormValues } from '@/routes/create-session/types';
import { getSessionStatusText } from '@/utils/getSessionStatusText';
import getStatusStyle from '@/utils/getStatusStyle';

import {
  ActionBoxDataType,
  InfoBoxDataType,
  IntervieweeCardContainerProps,
  LINK_TYPE,
  ProfileCardDataType,
} from './types';

const IntervieweeCardContainer: React.FC<IntervieweeCardContainerProps> = ({
  intervieweeName,
  intervieweeEmail,
  status,
  position,
  duration,
  id: sessionId,
  refId,
  refUrl,
  expiredAt,
  availableUntil,
  viewer = false,
  intervieweeUrl,
  backofficeInterviewerUrl,
  viewerUrl,
  viewerUrlExpiredAt,
  highlightCount,
  refetchSession,
  refetchQuestionSnapshots,
  isSessionDataLoading,
  rerecordingStatus,
}) => {
  const [isResetSessionModalOpen, setIsResetSessionModalOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  const [resetInterviewSession, { loading: isResetInterviewLoading, error: resetInterviewError }] =
    useResetInterviewSessionMutation();

  const [refreshToken, { loading: refreshTokenLoading }] = useCreateSessionViewTokenMutation({});

  const handleRequestSessionAccessToken = async () => {
    await refreshToken({ variables: { createSessionViewTokenInput: { sessionId } } });

    if (typeof refetchSession === 'function') {
      refetchSession();
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const handleModalOpen = () => {
    setIsResetSessionModalOpen(true);
  };

  const handleModalClose = () => {
    setIsResetSessionModalOpen(false);
  };

  const handleResetInterview = async (data: FormValues) => {
    try {
      await resetInterviewSession({
        variables: {
          resetInterviewSessionInput: {
            duration: data.duration,
            expiredAt: new Date(data.expiredAt),
            sessionId,
          },
        },
      });
    } catch (error) {
      console.error('Error resetting interview session:', error);
    }

    handleModalClose();

    if (typeof refetchSession === 'function') {
      refetchSession();
    }

    if (typeof refetchQuestionSnapshots === 'function') {
      refetchQuestionSnapshots();
    }
  };

  const statusText = getSessionStatusText(
    status as SessionStatus,
    availableUntil ? new Date(availableUntil) : null,
    new Date(expiredAt),
  );

  const statusStyle = getStatusStyle(statusText);

  const infoBoxData: InfoBoxDataType[] = [
    {
      label: t(['intervieweeProfiles.position.label', 'intervieweeProfiles.position.viewerLabel']),
      value: position,
      shouldDisplay: true,
    },
    {
      label: 'สัมภาษณ์ได้ถึงวันที่',
      value: availableUntil ?? expiredAt,
      shouldDisplay: !viewer,
    },
  ];

  const actionBoxData: ActionBoxDataType[] = [
    {
      label: 'ลิงก์การสัมภาษณ์',
      description: 'ลิงก์สำหรับผู้สมัคร เพื่อเริ่มการสัมภาษณ์',
      urls: [
        {
          linkType: LINK_TYPE.URL_COPY,
          value: intervieweeUrl,
        },
      ],
      shouldDisplay: true,
    },
    {
      label: 'ลิงก์ดูผลสัมภาษณ์ (Admin)',
      description: 'ลิงก์สำหรับ Admin เพื่อเข้าดูผลสัมภาษณ์ และจัดการข้อมูล',
      urls: [
        {
          linkType: LINK_TYPE.URL_COPY,
          value: backofficeInterviewerUrl,
        },
      ],
      shouldDisplay: true,
    },
    {
      label: `ลิงก์ดูผลสัมภาษณ์ (บุคคลทั่วไปในระบบ)`,
      description: viewerUrlExpiredAt ? `ลิงก์หมดอายุ: ${viewerUrlExpiredAt}` : undefined,
      urls: [
        {
          linkType: LINK_TYPE.EXPIRY_LINK_COPY,
          value: viewerUrl ?? '',
        },
      ],
      handleRefreshUrl: handleRequestSessionAccessToken,
      isLoading: refreshTokenLoading,
      shouldDisplay: status !== SessionStatus.NotStarted,
    },
    {
      label: `ล้างข้อมูลการสัมภาษณ์`,
      description: 'ล้างข้อมูลการสัมภาษณ์ทั้งหมด และตั้งค่าวันหมดอายุใหม่',
      urls: [
        {
          linkType: LINK_TYPE.ACTIVATE_MODAL,
          value: handleModalOpen,
        },
      ],
      shouldDisplay: true,
    },
  ];

  const profileCardData: ProfileCardDataType = {
    refId,
    intervieweeEmail,
    intervieweeName,
    statusText,
    statusStyle,
    highlightCount,
    infoBoxData,
    refUrl,
    rerecordingStatus,
  };

  const resetModalData = {
    isOpen: isResetSessionModalOpen,
    isResetInterviewLoading,
    resetInterviewError,
    handleModalClose,
    intervieweeName,
    intervieweeEmail,
    position,
    refId,
    register,
    errors,
    handleSubmit,
    handleResetInterview,
    duration,
  };

  if (isSessionDataLoading) {
    return <IntervieweeCardSkeleton />;
  }

  return (
    <>
      <IntervieweeCard
        profileCardData={profileCardData}
        actionBoxData={actionBoxData}
        viewer={viewer}
      />
      {isResetSessionModalOpen ? <ResetInterviewModal {...resetModalData} /> : null}
    </>
  );
};

export default IntervieweeCardContainer;
